import React, { useEffect, useState } from 'react';
import { render } from 'react-dom';
import classNames from 'classnames';
import Carousel from '@brainhubeu/react-carousel';
import Modal from 'react-modal';
import { InView } from 'react-intersection-observer';
import DataProvider from '../components/DataProvider';
import IntlProvider from '../components/IntlProvider';
import Slide from './Slide';
import Arrow from './Arrow';
import {
  useDebouncedResizeObserver,
  fetchOptions,
  NETWORK_LABELS,
  getMediaType,
  setGiggReferrer,
  openPostOnRedirect,
} from '../utils';
import '@brainhubeu/react-carousel/lib/style.css';
import Spinner from '../components/Spinner';
import '../iconSetup';
import styles from './Carousel.module.scss';
import {
  AnalyticsEvents,
  AnalyticsMaxPollTime,
  AnalyticsPollTime,
  AnalyticsScores,
  EmbedTypes,
} from '../consts';
import ModalEmbed from '../components/Modal/ModalEmbed';
import { ModalProvider } from '../components/ModalProvider/ModalProvider';
import 'wicg-inert';

const { REACT_APP_CORE_API_URL } = process.env;
const { REACT_APP_GIGG_ANALYTICS_KEY } = process.env;
const { REACT_APP_ENV } = process.env;

let inViewPrev = false;
let isFlipped = false;
let postsFocused = 0;

function LimitProvider({ children, config }) {
  const [options, setOptions] = useState();
  useEffect(async () => {
    const preview = config.preview ? 'preview/' : '';
    const results = await fetchOptions(
      `${REACT_APP_CORE_API_URL}/fanboard/${preview}${config.accountId}/${config.folderId}/options`
    );

    setOptions(results);
  }, []);

  if (options) {
    return children(options.carouselPostLimit ?? 15);
  }

  return null;
}

function GiggCarousel({
  options,
  posts,
  loading,
  onMediaError,
  formatDistanceLocale,
  config,
  showPostMedia,
  handleModalNavigate,
  modalIsOpen,
  openModal,
  closeModal,
  selectedPost,
  referredPost,
  getReferredPost,
}) {
  Modal.setAppElement('body');
  const {
    carouselAutoplay = false,
    carouselInterval = 8,
    carouselInfinite = true,
    carouselHideSocialIcon = false,
  } = options;
  const autoPlay =
    typeof carouselInterval !== 'number' || !carouselAutoplay ? undefined : carouselInterval * 1000;
  const slideWidth = 250;
  const { ref, width } = useDebouncedResizeObserver(500);
  const allSlidesWidth = posts.length * slideWidth;
  const slidesPerPage = allSlidesWidth < width ? 3 : Math.floor(width / slideWidth);
  const slidesPerScroll = allSlidesWidth < width ? 1 : Math.ceil(slidesPerPage * 0.5);
  const embedType = EmbedTypes.CAROUSEL;

  useEffect(() => {
    openPostOnRedirect(posts, openModal, getReferredPost, config, EmbedTypes.CAROUSEL);
  }, []);

  const handleInViewChange = async (inView) => {
    if (!inView && inViewPrev) {
      inViewPrev = inView;
      await giggAnalytics.logEvent(AnalyticsEvents.EMBED_UNVIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.CAROUSEL,
        folderId: __gigg.folderId,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
        inViewEvent: {
          _id: window.latestViewEvent?._id,
          createdDate: window.latestViewEvent?.createdDate,
        },
      });
      window.latestViewEvent = {};
      return window.latestViewEvent;
    }
    if (!inView) {
      return null;
    }
    inViewPrev = inView;
    window.latestViewEvent = await giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.CAROUSEL,
      folderId: __gigg.folderId,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
    });
    return window.latestViewEvent;
  };

  const handleCardFocus = (evt, item, index) => {
    if (
      evt.elapsedTime < 0.8 ||
      evt.propertyName !== 'transform' ||
      evt.srcElement.classList.length < 2 ||
      evt.srcElement.classList[1] !== 'gigg-carousel__slide-image'
    ) {
      return;
    }

    if (!isFlipped) {
      isFlipped = true;
      postsFocused += 1;
      giggAnalytics.logEvent(AnalyticsEvents.FOCUS_POST, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.GRID,
        folderId: __gigg.folderId,
        postIndex: index,
        postId: item.id,
        postType: item.service,
        mediaType: getMediaType(item.media),
        isCarousel: item.media?.length > 1,
        hasCTA: !!item.customLink?.linkUrl,
        scoreWeight:
          postsFocused > 1 ? AnalyticsScores.SECOND_FOCUS_POST : AnalyticsScores.FOCUS_POST,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
        inViewEvent: {
          _id: window.latestViewEvent?._id,
          createdDate: window.latestViewEvent?.createdDate,
        },
      });
      return;
    }
    isFlipped = false;
  };

  const handleVisibilityChange = (el, isVisible) => {
    if (!isVisible) {
      /* eslint-disable */
      el.parentElement.tabIndex = -1;
    } else {
      el.parentElement.tabIndex = 0;
    }
  };

  useEffect(() => {
    window.giggAnalyticsLoaded.then(async () => {
      setGiggReferrer();
      window.giggAnalytics = new GiggAnalytics(REACT_APP_GIGG_ANALYTICS_KEY, {
        apiVersion: 1,
        env: REACT_APP_ENV,
        sessionKey: __gigg.folderId,
      });
      window.giggFirstEvent = await giggAnalytics.logEvent(AnalyticsEvents.EMBED_PAGE_VIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.CAROUSEL,
        folderId: __gigg.folderId,
      });
      const interval = setInterval(() => {
        giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW_POLL, {
          boardId: __gigg.accountId,
          boardType: EmbedTypes.CAROUSEL,
          folderId: __gigg.folderId,
          firstEvent: {
            _id: window.giggFirstEvent?._id,
            createdDate: window.giggFirstEvent?.createdDate,
          },
          inViewEvent: {
            _id: window.latestViewEvent?._id,
            createdDate: window.latestViewEvent?.createdDate,
          },
          isPoll: true,
          basePollFilter: 'firstEvent._id',
        });
      }, AnalyticsPollTime);
      setTimeout(() => clearInterval(interval), AnalyticsMaxPollTime);
    });
  }, []);

  if (loading) {
    return (
      <div className={classNames(styles.carousel, 'gigg-carousel')} ref={ref}>
        <div className={classNames(styles.carouselSpinner, 'gigg-carousel__spinner')}>
          <Spinner loading />
        </div>
      </div>
    );
  }

  return (
    <div role="complementary" className={classNames(styles.carousel, 'gigg-carousel')} ref={ref}>
      <InView as="div" onChange={handleInViewChange}>
        <Carousel
          slidesPerPage={slidesPerPage}
          slidesPerScroll={slidesPerScroll}
          slideWidth={slideWidth}
          arrowLeft={<Arrow direction="left" />}
          arrowLeftDisabled={<Arrow direction="left" disabled />}
          arrowRight={<Arrow direction="right" />}
          arrowRightDisabled={<Arrow direction="right" disabled />}
          offset={20}
          autoPlay={autoPlay}
          draggable={false}
          arrows
          infinite={carouselInfinite}
          centered={carouselInfinite}
          stopAutoPlayOnHover
          addArrowClickHandler
        >
          {posts.map((p, i) => {
            const networkLabel = NETWORK_LABELS[p.service];
            const ariaLabel = p.altText
              ? `${networkLabel} image. ${p.altText}`
              : `${networkLabel} post.`;

            return (
              <button
                type="button"
                className={classNames(styles.carouselSlide, 'gigg-carousel__slide', {
                  [styles.carouselSlide__NoCaption]: options.carouselHideCaptions,
                })}
                onTransitionEnd={(evt) => {
                  handleCardFocus(evt, p, i);
                }}
                onClick={() => openModal(i, p, embedType)}
                aria-label={ariaLabel}
                aria-describedby={`gigg-slide-${i}`}
                style={{ outlineColor: options.primaryColor || '#d8c17a' }}
              >
                <Slide
                  item={p}
                  index={i}
                  openModal={openModal}
                  id={p.id}
                  media={p.media}
                  service={p.service}
                  caption={p.caption}
                  time={p.time}
                  username={p.username}
                  customLink={p.customLink}
                  altText={p.altText}
                  color={options.primaryColor || '#d8c17a'}
                  disableHashtagLinks={options.disableHashtagLinks}
                  onMediaError={onMediaError}
                  disableDates={options.disableDates}
                  carouselHideCaptions={options.carouselHideCaptions}
                  type={p.service}
                  embedType={embedType}
                  onVisibleChange={handleVisibilityChange}
                  carouselHideSocialIcon={carouselHideSocialIcon}
                />
              </button>
            );
          })}
        </Carousel>
        <ModalEmbed
          referredPost={referredPost}
          openModal={openModal}
          selectedPost={selectedPost}
          boardId={config.accountId}
          showPostMedia={showPostMedia}
          formatDistanceLocale={formatDistanceLocale}
          closeModal={closeModal}
          options={options}
          itemCount={posts.length}
          onNavigate={handleModalNavigate}
          modalIsOpen={modalIsOpen}
          config={config}
          posts={posts}
          modalRepeat={carouselInfinite}
          embedType={embedType}
        />
      </InView>
    </div>
  );
}

export default render(
  <IntlProvider>
    {({ formatDistanceLocale }) => (
      <LimitProvider config={__gigg}>
        {(postLimit) => (
          <ModalProvider>
            {({
              showPostMedia,
              handleModalNavigate,
              modalIsOpen,
              openModal,
              closeModal,
              selectedPost,
              referredPost,
              getReferredPost,
            }) => (
              <DataProvider config={__gigg} postLimit={postLimit} mediaFilter="!text">
                <GiggCarousel
                  config={window.__gigg}
                  formatDistanceLocale={formatDistanceLocale}
                  showPostMedia={showPostMedia}
                  handleModalNavigate={handleModalNavigate}
                  modalIsOpen={modalIsOpen}
                  openModal={openModal}
                  closeModal={closeModal}
                  selectedPost={selectedPost}
                  referredPost={referredPost}
                  getReferredPost={getReferredPost}
                />
              </DataProvider>
            )}
          </ModalProvider>
        )}
      </LimitProvider>
    )}
  </IntlProvider>,
  document.getElementById(__gigg.domElementId)
);
